import { AlertCircleIcon } from "lucide-react";
import { type FC, memo, useEffect, useState } from "react";
import type React from "react";
import { Link } from "react-router";
import { Toggle } from "./Toggle";

interface IsConfiguredResult {
	isConfigured: boolean;
	text: React.ReactNode;
}

interface SettingItemProps {
	id: string;
	name: string;
	description?: string;
	enabled: boolean;
	requiresSetup: boolean;
	onToggle: (id: string) => void;
	setupMessage?: string;
	setupUrl?: string;
	alwaysEnabled?: boolean;
	isConfigured?: () => Promise<IsConfiguredResult>;
}
export const SettingItem: FC<SettingItemProps> = memo(
	({
		id,
		name,
		description,
		enabled,
		requiresSetup,
		onToggle,
		setupMessage,
		setupUrl,
		alwaysEnabled,
		isConfigured,
	}) => {
		const [isConfiguredResponse, setIsConfiguredResponse] = useState<
			IsConfiguredResult | undefined
		>(undefined);

		useEffect(() => {
			if (!isConfigured) return;
			isConfigured()
				.then(setIsConfiguredResponse)
				.catch(() => {});
		}, [isConfigured]);

		return (
			<div className="py-4 border-b border-gray-200 last:border-b-0">
				<div className="flex items-start justify-between">
					<div className="flex-grow pr-4">
						<div className="flex items-center">
							<h3 className="text-lg font-medium text-gray-900">{name}</h3>
							{requiresSetup && enabled && (
								<div className="ml-2 flex items-center text-yellow-600">
									<AlertCircleIcon size={18} />
									{setupUrl ? (
										<Link to={setupUrl} className="ml-1 text-sm">
											Setup {name} here
										</Link>
									) : (
										<span className="ml-1 text-sm">{setupMessage}</span>
									)}
								</div>
							)}
						</div>
						{description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
						{isConfiguredResponse?.isConfigured === true && (
							<div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
								<p className="text-sm text-green-700">{isConfiguredResponse?.text}</p>
							</div>
						)}
						{typeof !isConfiguredResponse?.isConfigured === "undefined" && (
							<div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
								<p className="text-sm text-red-700">{isConfiguredResponse?.text}</p>
							</div>
						)}
						{requiresSetup && enabled && setupMessage && (
							<div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
								<p className="text-sm text-yellow-700">{setupMessage}</p>
							</div>
						)}
					</div>
					<div className="flex-shrink-0 pt-1">
						<Toggle enabled={enabled || alwaysEnabled || false} onChange={() => onToggle(id)} />
					</div>
				</div>
			</div>
		);
	},
) as FC<SettingItemProps>;
